import { Message } from './Message';
import { StreamingMessage } from './StreamingMessage';
import { ProfileProgress } from './ProfileProgress';
import { ErrorMessage } from './ErrorMessage';
import { useChatStore } from '@/stores/chatStore';
import type { Message as MessageType, Artifact, ProfileProgress as ProfileProgressType } from '@/types';

interface MessageListProps {
  messages: MessageType[];
  isStreaming: boolean;
  streamingContent: string;
  streamingArtifacts: Artifact[];
  profilingProgress: ProfileProgressType | null;
  thinkingStatus?: string | null;
}

export function MessageList({
  messages,
  isStreaming,
  streamingContent,
  streamingArtifacts,
  profilingProgress,
  thinkingStatus,
}: MessageListProps) {
  const error = useChatStore((s) => s.error);

  // Never render an empty assistant turn (avatar + blank pill). This guards
  // both the live path and any empty rows a prior build may have persisted and
  // that reload pulls back from the DB. A message is renderable if it carries
  // text, artifacts, or interactive options — user turns always have content.
  const visible = messages.filter(
    (m) =>
      m.role !== 'assistant' ||
      (m.content?.trim().length ?? 0) > 0 ||
      (m.artifacts?.length ?? 0) > 0 ||
      !!m.interactive,
  );

  return (
    <div className="mx-auto max-w-3xl px-4">
      {visible.map((message, i) => (
        // Consecutive same-role messages render as one visual group: the avatar
        // shows once per run and follow-ups tuck closer, so a narration burst
        // reads as one story instead of a stack of disjoint notifications.
        <Message
          key={message.id}
          message={message}
          grouped={i > 0 && visible[i - 1]?.role === message.role}
        />
      ))}

      {isStreaming && profilingProgress && !streamingContent && (
        <ProfileProgress progress={profilingProgress} />
      )}

      {isStreaming && (streamingContent || !profilingProgress) && (
        <StreamingMessage
          content={streamingContent}
          artifacts={streamingArtifacts}
          thinkingStatus={thinkingStatus}
        />
      )}

      {error && !isStreaming && <ErrorMessage error={error} />}

      {messages.length === 0 && !isStreaming && !error && (
        <div className="p-8 text-center text-muted-foreground">
          Start a conversation with ZAC
        </div>
      )}
    </div>
  );
}
